<template>
  <div class="enter-code">
    <div v-if="signupText" class="ri-body margin-bottom-xxs">{{ signupText }}</div>
    <div class="ri-large-headline ri-alte margin-bottom-xxs">מה הקוד החד פעמי שקיבלת?</div>
    <div class="ri-large-body margin-bottom-l">
      <div v-if="sendCodeViaSMS">
        <div>שלחנו לך קוד למספר {{ formattedPhone }}</div>
      </div>
      <div v-else>
        <div>שלחנו לך קוד {{ otpSentVia }} למספר <div>{{ formattedPhone }}</div></div>
      </div>
    </div>

    <text-input
      class="margin-top-l"
      :autofocus="true"
      label-text="קוד 6 ספרות"
      :value="code"
      :onInput="onInput"
      :error="error"
      :inputPattern="'\\d*'"
      :error-message="errorLabel"
      inputType="tel"
      autocomplete="one-time-code" />

    <riseup-button :title="buttonTitle" @click="() => confirmCode()" :loading="loading" :disabled="buttonDisabled" :size="'small'"/>

    <div class="action" v-on:click="sendCodeAgain" v-if="!sendAgainLoading">לא קיבלתי את הקוד, שלחו לי שוב</div>
    <whatsapp-send-again v-if="!sendAgainLoading && sendCodeViaSMS"
      @click="() => sendCodeAgain(true)"
    />
    <div class="back" v-on:click="goBack">רגע, זה לא מספר הטלפון הנכון</div>
    <div class="loader" v-if="sendAgainLoading"/>

  </div>
</template>

<script>
import BaseUI from '@riseupil/base-ui';
import phoneNumberUtils from '@/utils/phoneNumber';
import OtpMixin from '@/mixins/OtpMixin.vue';
import DDLogs from '@/DDLogs';
import Segment from '../Segment';
import WhatsappSendAgain from './WhatsappSendAgain.vue';

const ERRORS = {
  CODE_EXPIRED: 'CODE_EXPIRED',
  INVALID_CODE: 'INVALID_CODE',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
};

export default {
  name: 'EnterCode',
  mixins: [OtpMixin],
  components: {
    RiseupButton: BaseUI.V2Components.RiseupButton,
    TextInput: BaseUI.V2Components.TextInput,
    WhatsappSendAgain,
  },
  props: {
    signupText: {
      type: String,
      required: false,
    },
    phoneNumber: {
      type: String,
      required: true,
    },
    confirmCodeAction: {
      type: Function,
      required: true,
    },
    regenerateCodeAction: {
      type: Function,
      required: true,
    },
    serverError: {
      type: String,
      required: true,
    },
    resetError: {
      type: Function,
      required: true,
    },
    buttonTitle: {
      type: String,
      required: true,
    },
    loading: {
      type: Boolean,
      required: false,
    },
  },
  data() {
    return {
      code: '',
      sendAgainLoading: false,
      triedAutoConfirm: false,
      abortController: new AbortController(),
    };
  },
  created() {
    if (this.sendCodeViaSMS && 'OTPCredential' in window) {
      navigator.credentials.get({
        otp: { transport: ['sms'] },
        signal: this.abortController.signal,
      }).then(otp => {
        this.code = otp.code;
        this.confirmCode();
      }).catch(err => {
        DDLogs.log('Failed autofilling otp', err);
      });
    }
  },
  computed: {
    buttonDisabled() {
      return this.code.length !== 6 || this.loading;
    },
    codeContainsOnlyNumbers() {
      return /^([0-9]*)$/.test(this.code);
    },
    error() {
      return !!this.serverError || !this.codeContainsOnlyNumbers;
    },
    errorLabel() {
      if (this.serverError === ERRORS.CODE_EXPIRED) {
        Segment.trackUserGot('expiredCodeError');
        return 'הקוד פג תוקף';
      } if (this.serverError === ERRORS.INVALID_CODE) {
        Segment.trackUserGot('invalidCodeError', { invalidCode: this.code });
        return 'הקוד שהזנת לא תקין';
      } if (!this.codeContainsOnlyNumbers) {
        return 'הקוד צריך להכיל ספרות בלבד';
      } if (this.serverError === ERRORS.UNKNOWN_ERROR) {
        return 'נראה שקרתה תקלה, נסו שוב בהמשך';
      }
      return null;
    },
    formattedPhone() {
      return phoneNumberUtils.visualizePhoneNumber(this.phoneNumber);
    },
  },
  methods: {
    onInput(value) {
      this.code = value.trim();
      this.resetError();
      if (!this.triedAutoConfirm && this.code.length === 6) {
        this.triedAutoConfirm = true;
        this.confirmCode();
      }
    },
    async sendCodeAgain(useWhatsapp) {
      this.sendAgainLoading = true;
      const sendPlatform = useWhatsapp === true ? 'whatsapp' : this.sendPlatform;
      Segment.trackUserInteraction('GenerateCodeAgainClicked', { sendPlatform });
      Segment.trackUserInteraction('buttonClicked', {
        componentName: 'EnterCode',
        buttonText: 'רגע, זה לא מספר הטלפון הנכון',
      });
      await this.regenerateCodeAction(sendPlatform);
      this.sendAgainLoading = false;
    },
    async confirmCode() {
      this.abortController.abort();
      Segment.trackUserInteraction('buttonClicked', {
        componentName: 'EnterCode',
        buttonText: 'אישור',
      });
      this.confirmCodeAction(this.code);
    },
    goBack() {
      this.$router.back();
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '~@riseupil/base-ui/src/scss/riseup-colors';
  @import '~@riseupil/base-ui/src/scss/spacings';
  @import '~@riseupil/base-ui/src/scss/typography';
  @import '../scss/mixins';

  .enter-code {

    .notice {
      color: $riseup_red;
    }

    .riseup-button {
      width: 100%;
      margin: 10px 0px;
    }

    .action {
      margin: 20px 0px;
      color: $riseup_blue;
      font-weight: bold;
      cursor: pointer;
      font-size: 16px;
      text-align: center;

      &:active {
        color: $riseup_dark_blue;
      }
    }

    .back {
      margin: 20px 0px;
      color: $riseup_blue;
      font-weight: normal;
      cursor: pointer;
      font-size: 16px;
      text-align: center;

      &:active {
        color: $riseup_dark_blue;
      }
    }

    .loader {
      margin: 0 auto;
      width: 34px;
      height: 34px;
      border: 4px solid $riseup_blue;
      border-top: 4px solid $riseup_white;
      border-radius: 50%;
      animation: spin 2s linear infinite;

      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    }
  }
</style>
